import authService from './authService';
import {
  Company,
  CompanyFormData,
  CompanyListFilters,
  CompanyListResult,
  InvoiceNumberControl,
  InvoiceNumberControlPayload,
} from '../types/company';
import { ApiResponse } from '../types/common';
import { CertificateLogEntry, CertificateResult } from '../types/certificate';

/**
 * Company Service — Domínio Compartilhado
 *
 * Serviço para consulta de empresas do grupo econômico.
 * Não é vinculado a nenhuma entidade específica (Pedidos de Compra, NFs, etc.)
 * pois será reutilizado por diversas áreas do sistema.
 */

class CompanyService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private buildListParams(filters?: CompanyListFilters): string {
    const params = new URLSearchParams();
    params.set('page', String(filters?.page ?? 1));
    params.set('page_size', String(filters?.pageSize ?? 20));
    if (filters?.searchName) params.set('q', filters.searchName);
    if (filters?.searchDocument) params.set('document', filters.searchDocument);
    return params.toString();
  }

  /**
   * Retorna todas as empresas do grupo econômico.
   * Endpoint: GET /api/companies
   */
  async getCompanies(): Promise<Company[]>;
  async getCompanies(filters: CompanyListFilters): Promise<CompanyListResult>;
  async getCompanies(filters?: CompanyListFilters): Promise<Company[] | CompanyListResult> {
    const apiClient = await this.getApiClient();
    const endpoint = filters ? `/companies?${this.buildListParams(filters)}` : '/companies';
    const response = await apiClient.get(endpoint);
    if (!response.ok) throw new Error('Failed to fetch companies');
    const data: ApiResponse<Company[] | CompanyListResult> = await response.json();
    const payload = data.data;

    if (!filters) {
      if (Array.isArray(payload)) return payload;
      if (payload && 'data' in payload) return payload.data;
      return [];
    }

    const paginated = payload && !Array.isArray(payload) && 'data' in payload
      ? payload
      : { data: Array.isArray(payload) ? payload : [], pagination: {} };
    const pagination = (paginated.pagination ?? {}) as {
      page?: number;
      pageSize?: number;
      page_size?: number;
      total?: number;
      total_items?: number;
      totalPages?: number;
      total_pages?: number;
    };

    return {
      data: paginated.data,
      pagination: {
        page: pagination.page ?? filters.page ?? 1,
        pageSize: pagination.pageSize ?? pagination.page_size ?? filters.pageSize ?? 20,
        total: pagination.total ?? pagination.total_items ?? paginated.data.length,
        totalPages:
          pagination.totalPages ??
          pagination.total_pages ??
          1,
      },
    };
  }

  /**
   * Retorna uma empresa pelo ID.
   * Endpoint: GET /api/companies/:id
   */
  async getCompanyById(id: number): Promise<Company> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/companies/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Empresa não encontrada');
      throw new Error('Failed to fetch company');
    }
    const data: ApiResponse<Company> = await response.json();
    return data.data!;
  }

  async createCompany(payload: CompanyFormData): Promise<Company> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/companies', payload);
    if (!response.ok) {
      if (response.status === 409) throw new Error('Já existe uma empresa com este CNPJ');
      throw new Error('Failed to create company');
    }
    const data: ApiResponse<Company> = await response.json();
    return data.data!;
  }

  async updateCompany(id: number, payload: Partial<CompanyFormData>): Promise<Company> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/companies/${id}`, payload);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Empresa não encontrada');
      if (response.status === 403) throw new Error('Você não tem permissão para alterar estes campos');
      if (response.status === 409) throw new Error('Já existe uma empresa com este CNPJ');
      throw new Error('Failed to update company');
    }
    const data: ApiResponse<Company> = await response.json();
    return data.data!;
  }

  // ─── Numeração de NF-e (Financial.InvoiceNumberControl) por empresa ───

  private async unwrapError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      message = body?.message || body?.error || fallback;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  async listInvoiceNumberControls(companyId: number): Promise<InvoiceNumberControl[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/companies/${companyId}/invoice-number-controls`);
    if (!response.ok) return this.unwrapError(response, 'Falha ao carregar numeração das notas');
    const data: ApiResponse<InvoiceNumberControl[]> = await response.json();
    return data.data ?? [];
  }

  async createInvoiceNumberControl(companyId: number, payload: InvoiceNumberControlPayload): Promise<InvoiceNumberControl> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/companies/${companyId}/invoice-number-controls`, payload);
    if (!response.ok) return this.unwrapError(response, 'Falha ao criar numeração');
    const data: ApiResponse<InvoiceNumberControl> = await response.json();
    return data.data!;
  }

  async updateInvoiceNumberControl(companyId: number, payload: InvoiceNumberControlPayload): Promise<InvoiceNumberControl> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/companies/${companyId}/invoice-number-controls`, payload);
    if (!response.ok) return this.unwrapError(response, 'Falha ao atualizar numeração');
    const data: ApiResponse<InvoiceNumberControl> = await response.json();
    return data.data!;
  }

  async deleteInvoiceNumberControl(companyId: number, model: number, serie: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/companies/${companyId}/invoice-number-controls/${model}/${serie}`);
    if (!response.ok) await this.unwrapError(response, 'Falha ao remover numeração');
  }

  // ── Certificado digital A1 (e-CNPJ): assina NF-e e e-Social ──

  /**
   * POST /companies/:id/certificate — multipart com o arquivo e a senha.
   *
   * O apiClient força Content-Type JSON, então o multipart vai por fetch com
   * fallback cookie → token (mesmo padrão de bankIntegrationService).
   *
   * Arquivo e senha vão SEMPRE juntos: o backend abre um com a outra e confere
   * o CNPJ antes de gravar qualquer coisa.
   */
  async uploadCertificate(companyId: number, file: File, password: string): Promise<CertificateResult> {
    const { getConfig } = await import('../config');
    const baseUrl = getConfig().api.baseUrl;

    const fd = new FormData();
    fd.append('file', file);
    fd.append('password', password);

    const url = `${baseUrl}/companies/${companyId}/certificate`;
    const doFetch = async (token?: string | null) =>
      fetch(url, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) return this.unwrapError(response, 'Falha ao enviar o certificado');
    const data: ApiResponse<CertificateResult> = await response.json();
    return data.data!;
  }

  /**
   * POST /companies/:id/certificate/test — consulta o status do serviço da
   * SEFAZ usando o certificado gravado.
   *
   * O backend responde 200 mesmo quando o teste é negativo: o corpo carrega o
   * resultado da checagem, que é uma resposta e não uma falha da requisição.
   */
  async testCertificate(companyId: number): Promise<CertificateResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/companies/${companyId}/certificate/test`, {});
    if (!response.ok) return this.unwrapError(response, 'Falha ao testar o certificado');
    const data: ApiResponse<CertificateResult> = await response.json();
    return data.data!;
  }

  /** GET /companies/:id/certificate/logs */
  async getCertificateLogs(companyId: number, limit = 200): Promise<CertificateLogEntry[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/companies/${companyId}/certificate/logs?limit=${limit}`);
    if (!response.ok) return this.unwrapError(response, 'Falha ao carregar o histórico do certificado');
    const data: ApiResponse<CertificateLogEntry[]> = await response.json();
    return data.data ?? [];
  }
}

// Singleton
export const companyService = new CompanyService();
export default companyService;
